/*
 * ProfanityDetector — lexicon matcher over ASR word tokens, 9 languages.
 *
 * Pipeline per token (accuse -> excuse architecture):
 *   1. normalize — per-script: lowercase, ё→е, strip invisibles, Arabic
 *      tashkeel/alif fold, Hangul jamo compose, Latin diacritic fold,
 *      homoglyph fold, collapse repeats. Keeps letters of ANY script.
 *   2. ACCUSE: tier-bucketed root regexes from ProfanityLexicons[lang]
 *   3. EXCUSE: curated false-positive stems cancel substring-type accusations
 *      (психуй, Scunthorpe, mandarina, 시발점, كسر, pantai…)
 *   4. user whitelist always wins; user blacklist matched token-anchored
 *   5. FUZZY (RU only): Russian-Metaphone equality against standalone mat
 *      forms — recovers ASR vowel-confusion ("блидь" → блядь). Other languages
 *      ship NO fuzzy on purpose: measured against the real ASR corpus,
 *      edit-distance-1 maps Indonesian "bangsa" (nation) onto "bangsat" and
 *      French "fou" (mad) onto "fout". A censor that bleeps innocent words is
 *      worse than one that misses a hit the user can add by hand.
 *
 * Tier policy (RU tiers anchor on the Roskomnadzor legal definition):
 *   0 = hard profanity                     — always on
 *   1 = strong derivatives / slurs         — default on
 *   2 = rude but not profanity             — default off
 *   3 = euphemisms                         — default off
 *
 * Pure module: no DOM, works in window, Worker and node (tests).
 * ALL languages come from ProfanityLexicons — this file holds no rule copies.
 */
(function (root) {
    'use strict';

    /* ---------------- normalization ---------------- */

    var HOMOGLYPHS = {
        'a': 'а', 'b': 'в', 'c': 'с', 'e': 'е', 'h': 'н', 'k': 'к', 'm': 'м',
        'o': 'о', 'p': 'р', 't': 'т', 'x': 'х', 'y': 'у', 'u': 'и', '3': 'з', '0': 'о'
    };

    /* Latin diacritic fold. NOTE: ñ is deliberately ABSENT — folding it maps the
       Spanish coño onto `cono` ("cone"), an ordinary word in traffic-cone /
       ice-cream / geometry speech. Spanish rules spell ñ out instead.
       ß→ss is required (Scheiße→scheisse). */
    var DIACRITICS = {
        'à': 'a', 'á': 'a', 'â': 'a', 'ã': 'a', 'ä': 'a', 'å': 'a', 'æ': 'ae',
        'ç': 'c', 'è': 'e', 'é': 'e', 'ê': 'e', 'ë': 'e', 'ì': 'i', 'í': 'i',
        'î': 'i', 'ï': 'i', 'ò': 'o', 'ó': 'o', 'ô': 'o', 'õ': 'o',
        'ö': 'o', 'ø': 'o', 'œ': 'oe', 'ù': 'u', 'ú': 'u', 'û': 'u', 'ü': 'u',
        'ý': 'y', 'ÿ': 'y', 'ß': 'ss'
    };

    var INVISIBLES = /[\u200B‌‍\uFEFF\u00AD\u2060\u180E]/g;

    /* Arabic: strip harakat/tashkeel + tatweel, fold alif/hamza/ta-marbuta/
       alef-maksura (port of ProfanityEngine::normalizeArabic). */
    var AR_MARKS = /[ؐ-ًؚ-ٰٟۖ-ۜ۟-۪ۨ-ۭ]/g;
    var AR_FOLD = { 'أ': 'ا', 'إ': 'ا', 'آ': 'ا', 'ٱ': 'ا',
                    'ؤ': 'و', 'ئ': 'ي', 'ء': '', 'ة': 'ه', 'ى': 'ي' };
    function normalizeArabic(s) {
        s = s.replace(AR_MARKS, '').replace(/ـ/g, '');
        return s.replace(/[أإآٱؤئءةى]/g, function (ch) { return AR_FOLD[ch]; });
    }

    /* Korean: compose decomposed conjoining jamo so an NFD bypass matches the
       precomposed rule (port of ProfanityEngine::composeHangul). */
    function composeHangul(s) {
        var L = 0x1100, V = 0x1161, T = 0x11A7, S = 0xAC00, LC = 19, VC = 21, TC = 28, NC = VC * TC;
        var out = '', i = 0, n = s.length;
        while (i < n) {
            var c = s.charCodeAt(i);
            if (c >= L && c < L + LC && i + 1 < n) {
                var c2 = s.charCodeAt(i + 1);
                if (c2 >= V && c2 < V + VC) {
                    var t = 0;
                    if (i + 2 < n) {
                        var c3 = s.charCodeAt(i + 2);
                        if (c3 > T && c3 < T + TC) t = c3 - T;
                    }
                    out += String.fromCharCode(S + (c - L) * NC + (c2 - V) * TC + t);
                    i += t ? 3 : 2;
                    continue;
                }
            }
            out += s[i]; i++;
        }
        return out;
    }

    function normalizeToken(text, lang) {
        var s = String(text || '').toLowerCase();
        s = s.replace(INVISIBLES, '');
        s = s.replace(/ё/g, 'е');
        if (/[؀-ۿݐ-ݿ]/.test(s)) s = normalizeArabic(s);
        if (/[ᄀ-ᇿꥠ-꥿ힰ-퟿]/.test(s)) s = composeHangul(s);

        var hasCyr = /[а-я]/.test(s);
        var hasLat = /[a-z]/.test(s);
        if (hasCyr && hasLat) {
            // mixed-script word: fold Latin lookalikes into Cyrillic
            s = s.replace(/[abcehkmoptxyu30]/g, function (ch) { return HOMOGLYPHS[ch] || ch; });
            hasLat = /[a-z]/.test(s);
        }
        if (hasLat && !hasCyr) {
            s = s.replace(/[àáâãäåæçèéêëìíîïòóôõöøœùúûüýÿß]/g, function (ch) { return DIACRITICS[ch] || ch; });
        }
        // keep letters of ANY script (Latin, Cyrillic, Hangul, Arabic) + digits
        s = s.replace(/[^\p{L}\p{N}]/gu, '');
        s = s.replace(/ـ/g, '');              // tatweel is \p{Lm}: survives the class above
        s = s.replace(/(.)\1{2,}/gu, '$1$1');      // блииин → блиин, fuuuck → fuuck
        /* Russian: a doubled VOWEL is a drawn-out cry, not spelling.
           The generic rule above only collapses 3+ down to TWO, so «бляя» —
           the commonest stretched form — never matched /^бля$/ and went out
           uncensored (complaint #1337: «мат "бля" почти всегда не блюрит»).
           Vowels only, Cyrillic only: doubled CONSONANTS are spelling here
           (ванна, суббота), and collapsing those is what once mis-flagged the
           French salopette. Measured on the 146 098-word RU hunspell list:
           catches 26/26 stretched forms and flags FEWER ordinary words than
           before (91 vs 93 — «змееборец» stops matching as a side effect). */
        if (/[а-я]/.test(s)) s = s.replace(/([аеиоуыэюя])\1+/g, '$1');
        return s;
    }

    /* ---------------- rule packs ---------------- */
    /* Architecture follows the classic accuse/excuse design (PHP-Matotest /
     * py-censure lineage); the pattern content is written in-house and lives
     * in profanity-lexicons.js. */

    /* NO rule copies live here.
     *
     * They used to: RU/EN duplicates sat in this file as a "standalone node"
     * fallback while ProfanityLexicons carried the real ones. On 2026-08-09 that
     * cost an hour — edits to the copy changed nothing on the page, and the
     * measurement honestly reported "91 → 91" while I looked for the bug
     * elsewhere. Two lists of the same truth WILL drift; the loud failure of a
     * missing pack is cheaper than a silent disagreement.
     * Every language, including RU and EN, comes from profanity-lexicons.js.
     */

    /* ---------------- Russian Metaphone (Kankowski) ---------------- */

    function metaphoneRU(word) {
        var s = word;
        s = s.replace(/[ъь]/g, '');
        s = s.replace(/(йо|ио|йе|ие)/g, 'и');
        s = s.replace(/[оыя]/g, 'а');
        s = s.replace(/[еёэ]/g, 'и');
        s = s.replace(/ю/g, 'у');
        // devoice at word end / before voiceless-ish consonant
        var VOICED = { 'б': 'п', 'з': 'с', 'д': 'т', 'в': 'ф', 'г': 'к', 'ж': 'ш' };
        var out = '';
        for (var i = 0; i < s.length; i++) {
            var ch = s[i], next = s[i + 1];
            if (VOICED[ch] && (next === undefined || ('пстфкшхцчщ'.indexOf(next) >= 0))) {
                out += VOICED[ch];
            } else { out += ch; }
        }
        out = out.replace(/(тс|дс)/g, 'ц');
        out = out.replace(/(.)\1+/g, '$1');
        return out;
    }

    function damerauLevenshtein(a, b, max) {
        var la = a.length, lb = b.length;
        if (Math.abs(la - lb) > max) return max + 1;
        var d = [];
        for (var i = 0; i <= la; i++) { d[i] = [i]; }
        for (var j = 0; j <= lb; j++) { d[0][j] = j; }
        for (i = 1; i <= la; i++) {
            for (j = 1; j <= lb; j++) {
                var cost = a[i - 1] === b[j - 1] ? 0 : 1;
                d[i][j] = Math.min(d[i - 1][j] + 1, d[i][j - 1] + 1, d[i - 1][j - 1] + cost);
                if (i > 1 && j > 1 && a[i - 1] === b[j - 2] && a[i - 2] === b[j - 1]) {
                    d[i][j] = Math.min(d[i][j], d[i - 2][j - 2] + 1);
                }
            }
        }
        return d[la][lb];
    }

    /* Node/test path: pull the language packs from the sibling module. */
    function tryRequireLexicons() {
        try { return require('./profanity-lexicons.js'); } catch (e) { return null; }
    }

    /* ---------------- detector ---------------- */

    function create(opts) {
        opts = opts || {};
        // Language packs live in ProfanityLexicons (loaded alongside this file).
        // RU/EN stay built-in so the module keeps working standalone in node tests.
        var packs = (typeof root !== 'undefined' && root.ProfanityLexicons)
            ? root.ProfanityLexicons
            : (typeof require === 'function' ? tryRequireLexicons() : null);
        var lang = String(opts.lang || 'ru');
        var pack = packs && packs.get ? packs.get(lang) : null;
        if (!pack) {
            // No pack = the lexicon file did not load. Say so once instead of
            // quietly censoring nothing (or, worse, censoring by a stale copy).
            try { console.error('[profanity] no lexicon pack for "' + lang + '" — is profanity-lexicons.js loaded?'); } catch (e) { /* noop */ }
            pack = { rules: [], excuses: [], fuzzy: [] };
        }
        var rules = pack.rules || [];
        var excuses = pack.excuses || [];
        // Fuzzy recovery is RU-only by measurement — see the header note.
        var fuzzyTargets = lang === 'ru' ? (pack.fuzzy || []) : [];
        var tiersOn = Object.assign({ 0: true, 1: true, 2: false, 3: false }, opts.tiers || {});
        var fuzzyEnabled = opts.fuzzy !== false && lang === 'ru';

        // user lists: a trailing * means prefix-match, otherwise exact match
        function parseUserList(arr) {
            return (arr || []).map(function (s) {
                s = String(s).trim();
                var prefix = /\*\s*$/.test(s);
                var w = normalizeToken(s.replace(/\*+\s*$/, ''), lang);
                return w ? { w: w, prefix: prefix } : null;
            }).filter(Boolean);
        }
        var blacklist = parseUserList(opts.blacklist);
        var whitelist = parseUserList(opts.whitelist);

        var fuzzyPre = fuzzyTargets.map(function (t) {
            return { w: t.w.replace(/ё/g, 'е'), tier: t.tier, mp: metaphoneRU(t.w.replace(/ё/g, 'е')) };
        });

        function isExcused(norm) {
            for (var i = 0; i < excuses.length; i++) if (excuses[i].test(norm)) return true;
            return false;
        }

        /**
         * scanToken(rawText, conf) ->
         *   null | {ruleId, tier, kind:'exact'|'fuzzy'|'custom'|'masked', target?}
         */
        function scanToken(rawText, conf) {
            var raw = String(rawText || '');
            // masked profanity straight from the ASR ("f***", "б****")
            if (/[a-zа-яё]\*{2,}/i.test(raw)) {
                return { ruleId: lang + '.masked', tier: 0, kind: 'masked' };
            }
            // hyphen compounds: scan parts separately ("какой-то",
            // "прошлое-настоящее-будущее" must not glue into a false root)
            if (/[-–—]/.test(raw)) {
                var parts = raw.split(/[-–—]+/);
                if (parts.length > 1) {
                    for (var p = 0; p < parts.length; p++) {
                        var sub = scanToken(parts[p], conf);
                        if (sub) return sub;
                    }
                    return null;
                }
            }
            var norm = normalizeToken(raw, lang);
            if (!norm) return null;
            var hit = scanNorm(norm, conf);
            if (hit) return hit;
            // Drawn-out interjections («бляяя», «хуууй»): normalizeToken collapses
            // 3+ runs down to 2, leaving a doubled letter no pattern expects, so we
            // retry fully collapsed. GATE this on the RAW token actually containing
            // an elongation: collapsing unconditionally also squashes ORDINARY
            // double letters ("salopette"→"salopete", "chatte"→"chate"), which slips
            // straight past the (?!tte) guards and past the excuse list — measured
            // as a false positive on the French overalls "salopette".
            if (!/(.)\1\1/u.test(raw.toLowerCase())) return null;
            var collapsed = norm.replace(/(.)\1+/gu, '$1');
            if (collapsed !== norm) return scanNorm(collapsed, conf);
            return null;
        }

        function scanNorm(norm, conf) {
            for (var wl = 0; wl < whitelist.length; wl++) {
                var we = whitelist[wl];
                if (norm === we.w || (we.prefix && norm.indexOf(we.w) === 0)) return null;
            }
            for (var bl = 0; bl < blacklist.length; bl++) {
                var be = blacklist[bl];
                if (norm === be.w || (be.prefix && norm.indexOf(be.w) === 0)) {
                    return { ruleId: 'custom', tier: 0, kind: 'custom' };
                }
            }
            var excused = isExcused(norm);
            for (var r = 0; r < rules.length; r++) {
                var rule = rules[r];
                if (!tiersOn[rule.tier]) continue;
                for (var a = 0; a < rule.accuse.length; a++) {
                    if (rule.accuse[a].test(norm)) {
                        if (excused) break; // excused: skip this rule, try next
                        return { ruleId: rule.id, tier: rule.tier, kind: 'exact' };
                    }
                }
            }
            // Fuzzy = metaphone-EQUALITY only. Plain Levenshtein-2 on Russian is
            // far too loose (corpus scan: запись→заебись, музыки→мудаки,
            // единый→ебаный all at distance 2); metaphone keeps the consonant
            // skeleton, so it recovers exactly the vowel-confusion class of ASR
            // errors (ахуенно=охуенно) without those FPs.
            if (fuzzyEnabled && !excused && norm.length >= 4 && (conf === undefined || conf >= 0.5)) {
                var mp = metaphoneRU(norm);
                for (var f = 0; f < fuzzyPre.length; f++) {
                    var t = fuzzyPre[f];
                    if (!tiersOn[t.tier]) continue;
                    if (mp === t.mp) {
                        return { ruleId: lang + '.fuzzy', tier: t.tier, kind: 'fuzzy', target: t.w };
                    }
                }
            }
            return null;
        }

        /**
         * scan(words) — words: [{text, start, end, conf?}]
         * returns hits: [{index, text, start, end, ruleId, tier, kind, target?}]
         */
        function scan(words) {
            var hits = [];
            for (var i = 0; i < words.length; i++) {
                var w = words[i];
                var hit = scanToken(w.text, w.conf);
                if (hit) {
                    hits.push({
                        index: i, text: w.text, start: w.start, end: w.end,
                        ruleId: hit.ruleId, tier: hit.tier, kind: hit.kind, target: hit.target || null
                    });
                }
            }
            return hits;
        }

        return { scan: scan, scanToken: scanToken, normalizeToken: function (t) { return normalizeToken(t, lang); } };
    }

    var api = { create: create, metaphoneRU: metaphoneRU, damerauLevenshtein: damerauLevenshtein, _normalizeToken: normalizeToken };

    if (typeof module !== 'undefined' && module.exports) module.exports = api;
    else root.ProfanityDetector = api;
})(typeof self !== 'undefined' ? self : this);
